import { useEffect, useState, type RefObject } from 'react';

/**
 * True once the element has been meaningfully on screen. Latches: it never goes
 * back to false.
 *
 * WHY LATCHING. The caller is the hero demo's carousel, and "start when seen"
 * and "pause when scrolled away" are different features with different costs.
 * The carousel already carries four pieces of pause state (hovered, intent,
 * budgetSpent, stopped); a fifth that fires on scroll would raise questions none
 * of the others answer — whether the tab's dwell and the hover budget drain
 * while hidden, and what a visitor should find on scrolling back. Starting once
 * is the whole of the problem that was actually worth solving.
 *
 * WHY THE DEFAULT rootMargin IS A PERCENTAGE. The demo's frame is 640-650px
 * tall and the viewports it must work in run down to ~667px, so a threshold
 * expressed as a FRACTION OF THE ELEMENT is fragile at the small end — the
 * element is nearly the size of the root. Trimming 30% off the root's bottom
 * edge instead asks a height-independent question: "has this reached the part of
 * the screen a reader is actually looking at?" It fires once the element crosses
 * into the top 70% of the viewport rather than the instant one pixel peeks over
 * the bottom, which is what stops the sequence beginning while the visitor is
 * still mid-scroll.
 *
 * SSR-SAFE by construction: effects do not run during the prerender, so this
 * returns false there — correct, since there are no timers to gate in a static
 * render. Where IntersectionObserver is missing it returns true immediately,
 * which degrades to the old always-on behaviour rather than to a demo that never
 * starts.
 */
export function useInViewOnce(
  ref: RefObject<Element | null>,
  rootMargin = '0px 0px -30% 0px'
): boolean {
  const [seen, setSeen] = useState(false);

  useEffect(() => {
    if (seen) return;
    const el = ref.current;
    if (!el) return;

    if (typeof IntersectionObserver === 'undefined') {
      setSeen(true);
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) setSeen(true);
      },
      { rootMargin }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [ref, rootMargin, seen]);

  return seen;
}
